import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSessionContext } from '../session/SessionContext.ts';
import { hostCue, LOBBY_WELCOME } from './hostCue.ts';
import { canStartHostPlayback, HostPlaybackAttempts } from './hostPlayback.ts';
const NarrationContext = createContext({ playing: false, text: '', enabled: true, available: false, canToggle: false, canReplay: false, error: '', toggle: () => {}, replayHost: () => {} });
export const useHostNarration = () => useContext(NarrationContext);
export function HostNarrationProvider({children}: {children: ReactNode}) {
  const {view} = useSessionContext(); const instance = view.child ?? view.root;
  const inLobby = view.session.status === 'lobby';
  const cue = inLobby ? { key: 'lobby/welcome', text: LOBBY_WELCOME } : instance && view.session.status === 'running' ? hostCue(instance.moduleId, instance.view) : null;
  const identity = cue ? `${inLobby ? view.session.id : instance!.instanceId}:${cue.key}` : '';
  const [enabled,setEnabled] = useState(() => {
    try { return localStorage.getItem('momentum.audio.host') !== 'off'; } catch { return true; }
  }); const [playing,setPlaying] = useState(false);
  const [error,setError] = useState(''); const replay = useRef(0);
  const audio = useRef<HTMLAudioElement | null>(null); const generation = useRef(0);
  const blocked = useRef(false);
  const attempts = useRef(new HostPlaybackAttempts()); const paused = view.session.pause !== null;
  const text = cue?.text ?? '';
  useEffect(() => {
    const element = new Audio(); element.preload='auto'; audio.current=element;
    element.onplay=()=>{setError('');setPlaying(true);}; element.onpause=()=>setPlaying(false); element.onended=()=>setPlaying(false);
    element.onerror=()=>{setPlaying(false);setError('This host line could not be loaded. Text remains available.');};
    return () => {generation.current++; element.pause(); element.removeAttribute('src'); element.load(); audio.current=null;};
  },[]);
  const [source,setSource] = useState<{identity:string;url:string}|null>(null);
  const pausedRef=useRef(paused); pausedRef.current=paused;
  function startPlayback(attemptKey: string, failureMessage: string) {
    const element = audio.current;
    if (!element || !attempts.current.claim(attemptKey)) return;
    // Reserve synchronously. React effects can run before play() settles; reserving only in `.then()` lets
    // one user gesture and the enabling effect start the same line twice on fast state updates.
    blocked.current = false;
    element.currentTime = 0;
    const token = generation.current;
    void element.play().catch(() => {
      if (token === generation.current) {
        attempts.current.release(attemptKey);
        blocked.current = true;
        setError(failureMessage);
      }
    });
  }
  useEffect(() => {
    const token=++generation.current; const element=audio.current;
    element?.pause(); element?.removeAttribute('src'); setPlaying(false);setError('');setSource(null);blocked.current=false;
    if(!identity)return;
    void crypto.subtle.digest('SHA-256',new TextEncoder().encode(text)).then(bytes=>{
      if(token!==generation.current)return;
      const hash=Array.from(new Uint8Array(bytes),b=>b.toString(16).padStart(2,'0')).join('');
      const url=`/voice/${hash}.mp3`;
      if(audio.current){audio.current.src=url;audio.current.load();}
      setSource({identity,url});
    }).catch(()=>{if(token===generation.current)setError('Host audio needs a secure connection. Text is still available.');});
    return ()=>{generation.current++;audio.current?.pause();};
  },[identity,text]);
  useEffect(()=>{
    const element=audio.current;
    if(!element || !enabled){element?.pause();return;}
    if(!canStartHostPlayback({enabled,paused:pausedRef.current,identity,sourceIdentity:source?.identity ?? null}))return;
    startPlayback(`${identity}:${replay.current}`, 'Tap Replay host to hear this line. Text remains available.');
    return ()=>{element.pause();};
  },[source,enabled,identity]);
  useEffect(()=>{
    const element=audio.current;
    if(!element || !enabled || !source || source.identity!==identity)return;
    if(paused)element.pause();
    else if(!element.ended){const token=generation.current;void element.play().catch(()=>{if(token===generation.current)setError('Tap Replay host to hear this line.');});}
  },[paused]);
  useEffect(()=>{
    const retry=()=>{
      if(!blocked.current || document.hidden || !canStartHostPlayback({enabled,paused:pausedRef.current,identity,sourceIdentity:source?.identity ?? null}))return;
      startPlayback(`${identity}:${replay.current}`, 'Tap Replay host to hear this line. Text remains available.');
    };
    window.addEventListener('pointerdown',retry,{capture:true});
    window.addEventListener('keydown',retry,{capture:true});
    return()=>{window.removeEventListener('pointerdown',retry,{capture:true});window.removeEventListener('keydown',retry,{capture:true});};
  },[enabled,identity,source]);
  function toggle(){
    if(enabled){audio.current?.pause();setEnabled(false);try{localStorage.setItem('momentum.audio.host','off');}catch{/* Optional preference storage. */}return;}
    if(!source || source.identity!==identity || paused)return;
    setEnabled(true);
    try{localStorage.setItem('momentum.audio.host','on');}catch{/* Optional preference storage. */}
    // The first play must happen in the button gesture for iOS/Safari. The effect is still needed for
    // later server-authored cues, after the user has granted audio playback for this element.
    const element=audio.current;
    if(element && element.currentTime>0 && !element.ended){
      const token=generation.current;
      void element.play().catch(()=>{if(token===generation.current)setError('Tap Replay host to hear this line. Text remains available.');});
    }else startPlayback(`${identity}:${replay.current}`, 'Tap Replay host to hear this line. Text remains available.');
  }
  function replayHost(){
    if(paused || !source || source.identity!==identity)return;
    replay.current+=1;
    // Direct invocation is intentional: mobile browsers require replay to originate in this click.
    startPlayback(`${identity}:${replay.current}`, 'Tap Replay host to hear this line. Text remains available.');
  }
  const available = inLobby || Boolean(instance && (cue || enabled) && ['decision-maker','hot-potato','momentum-board-v2'].includes(instance.moduleId) && view.session.status === 'running');
  return <NarrationContext.Provider value={{playing, text, enabled, available, error, toggle, replayHost,
    canToggle: enabled || Boolean(source && source.identity === identity && !paused),
    canReplay: Boolean(enabled && cue && !paused && source?.identity === identity),
  }}>
    {children}
  </NarrationContext.Provider>;
}
